import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Filter, Map as MapIcon, List as ListIcon } from "lucide-react";
import { api } from "@/lib/apiClient";
import RecommendationCard from "@/components/RecommendationCard";
import SurfMap from "@/components/SurfMap";
import { useAuth } from "@/context/AuthContext";

export default function Recommendations() {
  const [sp] = useSearchParams();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [minConfidence, setMinConfidence] = useState("all");
  const [hideExcluded, setHideExcluded] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const query = useMemo(() => {
    const lat = sp.get("lat");
    const lon = sp.get("lon");
    return {
      origin_lat: lat ? Number(lat) : (user?.profile?.home_lat ?? null),
      origin_lon: lon ? Number(lon) : (user?.profile?.home_lon ?? null),
      origin_place: sp.get("place") || null,
      date: sp.get("date") || new Date().toISOString().slice(0, 10),
      max_travel_minutes: Number(sp.get("max_travel_minutes") || 120),
      ability_override: sp.get("ability") || null,
    };
  }, [sp, user]);

  useEffect(() => {
    setItems(null);
    setErr("");
    api
      .post("/recommendations", query)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load recommendations"));
  }, [query]);

  const filtered = (items || []).filter((i) => {
    if (hideExcluded && i.hard_excluded) return false;
    if (minConfidence !== "all") {
      const rank = { low: 0, moderate: 1, high: 2 };
      if (rank[i.confidence] < rank[minConfidence]) return false;
    }
    return true;
  });

  const [selectedId, setSelectedId] = useState(null);
  const [mobileView, setMobileView] = useState("list"); // list | map
  const cardRefs = useRef({});
  useEffect(() => { cardRefs.current = {}; }, [filtered.length]);
  useEffect(() => {
    if (selectedId && cardRefs.current[selectedId]) {
      cardRefs.current[selectedId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedId]);

  const mapAreas = filtered.map((i) => ({
    id: i.surf_area_id,
    slug: i.surf_area_slug,
    name: i.surf_area_name,
    lat: i.lat,
    lon: i.lon,
    score: i.score,
    suitability: i.suitability,
    best_window_start: i.best_window_start,
    best_window_end: i.best_window_end,
    visibility: i.visibility,
    coordinates_approximate: i.coordinates_approximate,
    coordinates_hidden: i.coordinates_hidden,
  }));

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12" data-testid="recommendations-page">
      <div className="overline">Ranked for you · {query.date}</div>
      <h1 className="font-display text-3xl md:text-4xl text-teal-900 mt-2">
        Your best surf right now
      </h1>
      <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
        Ranked by personal suitability, not raw wave quality. Warnings and confidence are shown
        alongside every score.
      </p>

      <div className="mt-6 flex flex-wrap gap-3 items-center card-soft p-4">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <label className="text-xs font-semibold flex items-center gap-2">
          <input
            type="checkbox"
            data-testid="filter-hide-excluded"
            checked={hideExcluded}
            onChange={(e) => setHideExcluded(e.target.checked)}
          />
          Hide unsafe matches
        </label>
        <div className="flex items-center gap-2 text-xs">
          <span className="overline">Confidence:</span>
          {["all", "low", "moderate", "high"].map((c) => (
            <button
              key={c}
              onClick={() => setMinConfidence(c)}
              data-testid={`filter-conf-${c}`}
              className={`px-3 py-1 rounded-full border ${
                minConfidence === c
                  ? "bg-teal-800 text-white border-teal-800"
                  : "bg-white border-border text-muted-foreground hover:border-teal-800/50"
              }`}
            >
              {c === "all" ? "All" : c.charAt(0).toUpperCase() + c.slice(1) + "+"}
            </button>
          ))}
        </div>
        <div className="ml-auto flex md:hidden gap-1 rounded-full border border-border p-1 bg-white">
          <button
            data-testid="mobile-view-list"
            onClick={() => setMobileView("list")}
            className={`px-3 py-1 rounded-full text-xs flex items-center gap-1 ${mobileView === "list" ? "bg-teal-800 text-white" : ""}`}
          >
            <ListIcon className="w-3.5 h-3.5" /> List
          </button>
          <button
            data-testid="mobile-view-map"
            onClick={() => setMobileView("map")}
            className={`px-3 py-1 rounded-full text-xs flex items-center gap-1 ${mobileView === "map" ? "bg-teal-800 text-white" : ""}`}
          >
            <MapIcon className="w-3.5 h-3.5" /> Map
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-6">
        <div className={`grid gap-4 ${mobileView === "map" ? "hidden md:grid" : ""}`}>
          {err && (
            <div className="text-sm text-terracotta-700 bg-[#FDF3F0] rounded-md px-3 py-2" data-testid="rec-error">
              {err}
            </div>
          )}
          {items === null && !err && (
            <div className="py-16 text-center text-muted-foreground flex items-center justify-center gap-2" data-testid="rec-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Reading the ocean…
            </div>
          )}
          {items && filtered.length === 0 && (
            <div className="card-soft p-8 text-center" data-testid="rec-empty">
              <div className="font-display text-xl text-teal-900">No surf areas match right now</div>
              <p className="text-sm text-muted-foreground mt-2">
                Try widening your travel radius or dropping the confidence filter.
              </p>
            </div>
          )}
          {filtered.map((it, i) => (
            <div
              key={it.surf_area_id}
              ref={(el) => { if (el) cardRefs.current[it.surf_area_id] = el; }}
              onMouseEnter={() => setSelectedId(it.surf_area_id)}
              className={`rounded-2xl transition-all ${
                selectedId === it.surf_area_id ? "ring-2 ring-terracotta-500 ring-offset-2 ring-offset-background" : ""
              }`}
            >
              <RecommendationCard item={it} index={i} />
            </div>
          ))}
        </div>
        <div className={`sticky top-20 self-start ${mobileView === "list" ? "hidden md:block" : ""}`}>
          {mapAreas.length > 0 && (
            <SurfMap
              areas={mapAreas}
              selectedId={selectedId}
              onSelectArea={setSelectedId}
              height={520}
            />
          )}
        </div>
      </div>
    </div>
  );
}
